import { ArrowRight, User2, CalendarDays, Loader2 } from "lucide-react";
import { Button } from "@/design-system/components/ui/button";
import { Badge } from "@/design-system/components/ui/badge";
import { useTranslation } from "react-i18next";
import { useCreateBookingSession } from "./hooks/useCreateBookingSession";
import { redirectToApp } from "@/utils/appUrl";

interface PublicProfessional {
  id: string;
  name: string;
  imageUrl?: string | null;
  specialty?: string | null;
  specialtyColor?: string | null;
}

export function PublicClinicTeamList({
  professionals,
  clinicId,
}: {
  professionals?: PublicProfessional[];
  clinicId: string;
  clinicName: string;
}) {
  const { t } = useTranslation(["clinic", "booking-patient"]);
  const createSession = useCreateBookingSession();

  // Acción rápida: Reservar directamente con este especialista
  const handleSelectProfessional = async (proId: string) => {
    try {
      // Generamos la sesión puente en la BD de forma asíncrona
      const response = await createSession.mutateAsync({
        clinicId: clinicId,
        professionalId: proId,
        source: "DIRECT_LINK", // 🎯 Indicamos que es un enlace directo sin servicio
      });

      // Viajamos limpios usando el sessionId recién creado
      const targetPath = `/patient/bookings/landing?sessionId=${response.id}`;
      redirectToApp(targetPath);
    } catch (error) {
      console.error("Error al crear la sesión puente de reserva", error);
    }
  };

  if (!professionals?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 md:py-16 border-2 border-dashed rounded-2xl bg-muted/5 p-4 text-center">
        <User2 className="w-10 h-10 md:w-12 md:h-12 text-muted-foreground/20 mb-3" />
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          {t("clinic:tabs.team.empty", "Sin profesionales disponibles")}
        </h3>
      </div>
    );
  }

  return (
    <div className="grid gap-3 text-left">
      {professionals.map((pro) => (
        <div
          key={pro.id}
          className="group relative flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl md:rounded-2xl border bg-card border-border/40 hover:border-primary/30 hover:shadow-sm transition-all duration-300 gap-4 overflow-hidden"
        >
          {/* Indicador lateral estético */}
          <div className="absolute left-0 top-0 sm:top-1/2 sm:-translate-y-1/2 h-1.5 sm:h-8 w-full sm:w-1 rounded-b-full sm:rounded-b-none sm:rounded-r-full bg-primary/10 group-hover:bg-primary transition-colors" />

          {/* Bloque de Información Flexible */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 md:gap-6 flex-1 w-full pt-1 sm:pt-0">
            {/* 1. AVATAR Y NOMBRE */}
            <div className="flex items-center gap-3.5 w-full sm:w-auto sm:min-w-[260px] shrink-0">
              <div className="h-12 w-12 rounded-xl overflow-hidden border-2 border-muted shadow-sm shrink-0 bg-secondary/30 flex items-center justify-center transition-transform group-hover:scale-105">
                {pro.imageUrl ? (
                  <img
                    src={pro.imageUrl}
                    alt={pro.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-base font-black text-primary/40">
                    {pro.name?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              <div className="flex flex-col min-w-0 flex-1">
                <h4 className="font-black text-sm uppercase tracking-wider text-foreground/90 leading-tight truncate">
                  {pro.name}
                </h4>
                <p className="text-[11px] text-muted-foreground font-medium mt-0.5">
                  {t(
                    "clinic:tabs.team.available",
                    "Disponible para cita online",
                  )}
                </p>
              </div>
            </div>

            {/* 2. ESPECIALIDAD (Limpia y sin roles de backend tipo ADMIN/USER) */}
            <div className="flex items-center gap-2 flex-wrap sm:flex-1 w-full sm:w-auto">
              {pro.specialty ? (
                <Badge
                  variant="outline"
                  className="rounded-lg text-[9px] font-black uppercase tracking-wider px-2.5 py-0.5"
                  style={{
                    color: pro.specialtyColor || "var(--primary)",
                    borderColor: `${pro.specialtyColor || "var(--primary)"}33`,
                    backgroundColor: `${pro.specialtyColor || "var(--primary)"}10`,
                  }}
                >
                  {pro.specialty}
                </Badge>
              ) : (
                <Badge
                  variant="secondary"
                  className="rounded-lg text-[9px] font-black uppercase tracking-wider opacity-60 px-2.5 py-0.5"
                >
                  {t("clinic:tabs.team.specialist", "Especialista")}
                </Badge>
              )}
            </div>
          </div>

          {/* 3. ACCIÓN: PEDIR CITA DIRECTA */}
          <div className="flex justify-end sm:block w-full sm:w-auto border-t sm:border-t-0 pt-3 sm:pt-0">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl opacity-100 sm:opacity-0 group-hover:opacity-100 transition-all hover:bg-primary hover:text-primary-foreground w-full sm:w-auto h-10 px-4 gap-2 font-bold text-xs uppercase tracking-wider shrink-0"
              onClick={() => handleSelectProfessional(pro.id)}
              disabled={createSession.isPending}
            >
              {createSession.isPending ? (
                <Loader2 size={14} className="shrink-0 animate-spin" />
              ) : (
                <CalendarDays size={14} className="shrink-0" />
              )}
              <span>{t("booking-patient:actions.book", "Pedir Cita")}</span>
              <ArrowRight
                size={14}
                className="shrink-0 hidden sm:inline-block ml-1"
              />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
